import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { NAVIGATION_ITEMS, COMPANY_INFO, COLORS } from '../constants';
import Container from './ui/Container';
import Button from './ui/button';

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const NavigationItem = ({ item, isLast }) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const triggerRef = useRef(null);

  if (!hasChildren) {
    return (
      <>
        <Link
          to={item.path}
          onClick={() => window.scrollTo(0, 0)}
          className="text-white hover:text-gray-300 nav-link-smooth font-medium px-4"
        >
          {item.label}
        </Link>
        {!isLast && <div className="w-px h-6 bg-red-500 mx-2"></div>}
      </>
    );
  }

  const dropdownId = `nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`;

  const openDropdown = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const closeDropdown = () => {
    closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 150);
  };

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <>
      <div
        className="relative"
        onMouseEnter={openDropdown}
        onMouseLeave={closeDropdown}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          ref={triggerRef}
          onFocus={openDropdown}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={dropdownId}
          className="bg-transparent border-0 cursor-pointer text-white hover:text-gray-300 nav-link-smooth font-medium px-4 inline-flex items-center gap-1.5"
        >
          {item.label}
          <ChevronIcon open={isOpen} />
        </button>

        <div
          id={dropdownId}
          role="menu"
          aria-label={`${item.label} submenu`}
          className={`absolute left-1/2 top-full mt-3 w-72 max-w-[92vw] -translate-x-1/2 rounded-md border border-white/10 shadow-lg py-2 z-50 transition-all duration-150 ${
            isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
          }`}
          style={{ backgroundColor: COLORS.secondary }}
        >
          {item.children.map((child) => (
            <Link
              key={child.label}
              to={child.path}
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                window.scrollTo(0, 0);
              }}
              className="block px-4 py-2.5 text-sm leading-snug text-white hover:bg-white hover:bg-opacity-10 hover:text-black"
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
      {!isLast && <div className="w-px h-6 bg-red-500 mx-2"></div>}
    </>
  );
};

const MobileMenuButton = ({ isOpen, onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden text-white p-2 relative z-50 hamburger-enhanced mobile-menu-button"
    aria-label="Toggle mobile menu"
  >
    <div className="w-6 h-6 flex flex-col justify-center items-center">
      <span
        className={`block w-6 h-0.5 bg-current hamburger-line ${
          isOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'
        }`}
      />
      <span
        className={`block w-6 h-0.5 bg-current hamburger-line ${
          isOpen ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <span
        className={`block w-6 h-0.5 bg-current hamburger-line ${
          isOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'
        }`}
      />
    </div>
  </button>
);

const MobileMenu = ({ isOpen, items, onClose }) => {
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setExpandedItem(null);
    }
  }, [isOpen]);

  const toggleExpanded = (path) => {
    setExpandedItem((current) => (current === path ? null : path));
  };

  return (
    <>
      {/* Slide-in Menu */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-56 max-w-[70vw] z-50 transform mobile-menu-slide-enhanced ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: COLORS.secondary }}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-end p-6">
          <button
            onClick={onClose}
            className="text-white p-2 hover:bg-white hover:bg-opacity-10 rounded-full button-smooth"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="py-6 pb-28 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 92px)' }}>
          {items.map((item, index) => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const animationClass = isOpen ? `mobile-menu-item-animated mobile-menu-item-${index + 1}` : '';

            if (!hasChildren) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    onClose();
                    window.scrollTo(0, 0);
                  }}
                  className={`block text-white hover:bg-white hover:bg-opacity-10 mobile-menu-item font-medium px-6 py-4 ${animationClass}`}
                >
                  {item.label}
                </Link>
              );
            }

            const isExpanded = expandedItem === item.path;
            const submenuId = `mobile-submenu-${item.label.toLowerCase().replace(/\s+/g, '-')}`;

            return (
              <div key={item.path} className={animationClass}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(item.path)}
                  aria-expanded={isExpanded}
                  aria-controls={submenuId}
                  className="flex items-center justify-between w-full text-left text-white hover:bg-white hover:bg-opacity-10 mobile-menu-item font-medium px-6 py-4"
                >
                  <span>{item.label}</span>
                  <ChevronIcon open={isExpanded} />
                </button>
                <div
                  id={submenuId}
                  className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[600px]' : 'max-h-0'}`}
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      to={child.path}
                      onClick={() => {
                        onClose();
                        window.scrollTo(0, 0);
                      }}
                      className="block text-white/90 hover:bg-white hover:bg-opacity-10 text-sm leading-snug pl-9 pr-4 py-3"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Menu Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <Button
            as="a"
            href={`tel:${COMPANY_INFO.phone}`}
            variant="secondary"
            size="small"
            className="w-full justify-center button-smooth"
            onClick={onClose}
          >
            📞 {COMPANY_INFO.phone}
          </Button>
        </div>
      </div>
    </>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle scroll for floating navbar (desktop only)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isDesktop = window.innerWidth >= 1024;
      
      if (isDesktop && scrollY > 100) {
        setShowFloatingNav(true);
      } else {
        setShowFloatingNav(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Main Header */}
      <header
        className="border-b z-40 relative shadow-sm"
        style={{
          backgroundColor: COLORS.secondary
        }}
      >
      <Container className="px-4 sm:px-10 relative">
        {/* Desktop Layout */}
        <div className="hidden lg:flex items-center justify-between py-1.5">
          {/* Logo */}
          <div className="flex-shrink-0 -ml-0 sm:-ml-4">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="block">
              <img
                className="h-18 w-auto cursor-pointer"
                src={COMPANY_INFO.logo}
                alt={`${COMPANY_INFO.name} Logo`}
              />
            </Link>
          </div>

          {/* Desktop Navigation and Phone */}
          <div className="flex items-center space-x-8 -mr-24">
            <nav className="flex items-center">
              {NAVIGATION_ITEMS.map((item, index) => (
                <NavigationItem
                  key={item.path}
                  item={item}
                  isLast={index === NAVIGATION_ITEMS.length - 1}
                />
              ))}
            </nav>

            {/* Phone Number */}
            <Button
              as="a"
              href={`tel:${COMPANY_INFO.phone}`}
              variant="secondary"
              size="small"
              className="button-smooth"
            >
              {COMPANY_INFO.phone}
            </Button>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden py-1.5">
          {/* Top row: Logo centered */}
          <div className="flex justify-center mb-1.5">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="block">
              <img
                className="h-16 w-auto cursor-pointer"
                src={COMPANY_INFO.logo}
                alt={`${COMPANY_INFO.name} Logo`}
              />
            </Link>
          </div>
          
          {/* Bottom row: Phone button on left, hamburger menu on right */}
          <div className="flex items-center justify-between">
            <Button
              as="a"
              href={`tel:${COMPANY_INFO.phone}`}
              variant="secondary"
              size="small"
              className="button-smooth"
            >
              {COMPANY_INFO.phone}
            </Button>
            
            <MobileMenuButton
              isOpen={isMobileMenuOpen}
              onClick={toggleMobileMenu}
            />
          </div>
        </div>

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          items={NAVIGATION_ITEMS}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      </Container>
      </header>

      {/* Floating Header - Desktop Only */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 shadow-lg hidden lg:block ${
          showFloatingNav ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{
          backgroundColor: COLORS.secondary
        }}
      >
        <Container className="px-4 sm:px-10 relative">
          <div className="flex items-center justify-between py-1.5">
            {/* Logo */}
            <div className="flex-shrink-0 -ml-0 sm:-ml-4">
              <Link to="/" onClick={() => window.scrollTo(0, 0)} className="block">
                <img
                  className="h-16 w-auto cursor-pointer"
                  src={COMPANY_INFO.logo}
                  alt={`${COMPANY_INFO.name} Logo`}
                />
              </Link>
            </div>

            {/* Desktop Navigation and Phone */}
            <div className="flex items-center space-x-8 -mr-24">
              <nav className="flex items-center">
                {NAVIGATION_ITEMS.map((item, index) => (
                  <NavigationItem
                    key={item.path}
                    item={item}
                    isLast={index === NAVIGATION_ITEMS.length - 1}
                  />
                ))}
              </nav>

              {/* Phone Number */}
              <Button
                as="a"
                href={`tel:${COMPANY_INFO.phone}`}
                variant="secondary"
                size="small"
                className="button-smooth"
              >
                {COMPANY_INFO.phone}
              </Button>
            </div>
          </div>
        </Container>
      </header>
    </>
  );
};

export default Header;